import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikConfig, FormikProvider, useFormik } from "formik";
import { RenderFormFields } from "src/components/RenderFormFields";
import { Button } from "src/components/Button";
import { ILoginValues } from "src/@types/auth";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { LOGIN_FORM_FIELDS, LOGIN_VALIDATION_SCHEMA } from "./constants";
import { loginPopupTrns } from "./trns";
import { AuthFormButton } from "../AuthFormButton";
import { useAppDispatch } from "src/hooks/redux";
import { loginUserAsync } from "src/redux/auth/actions";
import { getCurrentUserAsync } from "src/redux/currentUser/actions";
import { ModalState } from "src/hooks/useModal";
import { ModalWindowV2 } from "src/components/ModalWindowV2";

const INITIAL_VALUES = {
  email: "",
  password: "",
};

interface Props extends ModalState {
  onOpenPasswordResetPopup?: VoidFunction;
  onSuccessLogin?: VoidFunction;
}

export const LoginPopup: FC<Props> = ({
  onOpenPasswordResetPopup,
  onSuccessLogin,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const formikProps: FormikConfig<ILoginValues> = {
    initialValues: INITIAL_VALUES,
    validationSchema: LOGIN_VALIDATION_SCHEMA,
    onSubmit: (values, { resetForm }) => {
      dispatch(
        loginUserAsync({
          values,
          onSuccess: () => {
            dispatch(getCurrentUserAsync());
            resetForm();
            modalProps.closeModal();
            onSuccessLogin?.();
          },
        })
      );
    },
  };

  const formik = useFormik<ILoginValues>(formikProps);

  const onResetPassword = () => {
    modalProps.closeModal();
    formik.resetForm();
    onOpenPasswordResetPopup();
  };

  return (
    <ModalWindowV2
      {...modalProps}
      className="max-w-80 w-full"
      isShownOverlay
      isShownCloseButton
      isActivePortal
    >
      <FormikProvider value={formik}>
        <form className="flex flex-col gap-9 p-5 bg-white-base border border-gray-base/20 rounded-20">
          <h3 className="font-semibold text-center">
            {t(loginPopupTrns.title)}
          </h3>
          <div className="flex flex-col gap-9">
            <RenderFormFields fields={LOGIN_FORM_FIELDS} />
          </div>

          <div className="flex flex-col gap-3 text-base font-normal">
            <AuthFormButton>{t(loginPopupTrns.loginButton)}</AuthFormButton>
            <GoogleLoginButton />
          </div>

          <Button
            className="leading-none text-center text-purple-dark font-medium"
            type="button"
            onClick={onResetPassword}
          >
            {t(loginPopupTrns.resetPasswordButton)}
          </Button>
        </form>
      </FormikProvider>
    </ModalWindowV2>
  );
};
